import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import { COMPANY_INFO, HOME_FAQS } from "../data/companyData";
import { fetchCmsCollection, getOptimizedImageUrl } from "../lib/cms";
import FAQSection from "../components/FAQSection";
import LightboxModal from "../components/LightboxModal";
import QuickContactBanner from "../components/QuickContactBanner";

const DEFAULT_SERVICES = [
  {
    id: "s1",
    title: "Nyt tag & tagrenovering",
    tag: "Tagarbejde",
    service_slug: "nyt-tag-ribe",
    short_description: "Transformer dit hjem både æstetisk og funktionelt. Det beskytter ikke blot mod vejrets påvirkninger, men forbedrer også energiforbruget. Vi rådgiver om tegl, stål, eternit og tagpap.",
    featured_image: ["https://assets.auxodev.dk/assets/f0baa3a2-6eb6-4b57-8f55-7b5fb1f2d6b7/nyt-tegltag-paa-villa-400x284.jpg"],
    linkText: "Læs mere om nyt tag"
  },
  {
    id: "s2",
    title: "Nye vinduer og døre",
    tag: "Vinduer & Døre",
    service_slug: "nye-vinduer-og-doere-ribe",
    short_description: "En professionel installation eller udskiftning sikrer optimal isolering og sikkerhed, samtidig med at det giver et friskt udtryk til dit hjem. Træ, træ/alu eller PVC efter mål.",
    featured_image: ["https://assets.auxodev.dk/assets/f0baa3a2-6eb6-4b57-8f55-7b5fb1f2d6b7/klassisk-vinterhave-med-havestue.jpg"],
    linkText: "Læs mere om vinduer og døre"
  },
  {
    id: "s3",
    title: "Renovering af hus",
    tag: "Tømrer & Renovering",
    service_slug: "renovering-af-hus",
    short_description: "Skræddersyede tømrerløsninger der kombinerer funktionalitet med æstetik. Fra køkken- og badfornyelse til samlet hovedentreprise med koordinering af alle faggrupper.",
    featured_image: ["https://assets.auxodev.dk/assets/f0baa3a2-6eb6-4b57-8f55-7b5fb1f2d6b7/facade-renovering-stillads-tomrer-ribe-400x284.jpg"],
    linkText: "Læs mere om renovering"
  },
  {
    id: "s4",
    title: "Tilbygning & Vinterhave",
    tag: "Tilbygning",
    service_slug: "tilbygning-vinterhave",
    short_description: "Udvid boligen med en lys og harmonisk tilbygning eller vinterhave, skræddersyet til huset med godkendte konstruktioner.",
    featured_image: ["https://assets.auxodev.dk/assets/f0baa3a2-6eb6-4b57-8f55-7b5fb1f2d6b7/vinterhave-tilbygning-med-lys-og-glas-400x284.jpg"],
    linkText: "Læs mere om tilbygning"
  },
  {
    id: "s5",
    title: "Snedkerarbejde & Køkken",
    tag: "Snedkerarbejde",
    service_slug: "snedkerarbejde",
    short_description: "Eksklusive snedkerløsninger og håndbyggede køkkener i massivt træ tilpasset boligens rum, stil og individuelle behov.",
    featured_image: ["https://assets.auxodev.dk/assets/f0baa3a2-6eb6-4b57-8f55-7b5fb1f2d6b7/snedkerlavet-egetraeskoekken-med-teglgulv-400x284.jpg"],
    linkText: "Læs mere om snedkerarbejde"
  }
];

const DEFAULT_GALLERY = [
  {
    id: "g1",
    title: "Montering af tegltag med ovenlys",
    image: ["https://assets.auxodev.dk/assets/f0baa3a2-6eb6-4b57-8f55-7b5fb1f2d6b7/montering-af-tegltag-med-ovenlys.jpg"]
  },
  {
    id: "g2",
    title: "Nyt tegltag på villa",
    image: ["https://assets.auxodev.dk/assets/f0baa3a2-6eb6-4b57-8f55-7b5fb1f2d6b7/nyt-tegltag-paa-villa-400x284.jpg"]
  },
  {
    id: "g3",
    title: "Klassisk udestue og vinterhave",
    image: ["https://assets.auxodev.dk/assets/f0baa3a2-6eb6-4b57-8f55-7b5fb1f2d6b7/klassisk-vinterhave-med-havestue.jpg"]
  },
  {
    id: "g4",
    title: "Facaderenovering og stilladsarbejde",
    image: ["https://assets.auxodev.dk/assets/f0baa3a2-6eb6-4b57-8f55-7b5fb1f2d6b7/facade-renovering-stillads-tomrer-ribe-400x284.jpg"]
  },
  {
    id: "g5",
    title: "Snedkerdetaljer og specialløsninger",
    image: ["https://assets.auxodev.dk/assets/f0baa3a2-6eb6-4b57-8f55-7b5fb1f2d6b7/egetraeskoekken-med-indbyggede-skabe-400x284.jpg"]
  }
];

export default function HomePage() {
  const [services, setServices] = useState([]);
  const [galleryItems, setGalleryItems] = useState(DEFAULT_GALLERY);
  const [sponsorships, setSponsorships] = useState([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImgIndex, setCurrentImgIndex] = useState(0);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [currentHeroSlide, setCurrentHeroSlide] = useState(0);

  const heroSlides = [
    {
      image: "https://assets.auxodev.dk/assets/f0baa3a2-6eb6-4b57-8f55-7b5fb1f2d6b7/snedkerfirma-bil-toemrermester-jacob.jpg",
      alt: "Tømrermester Søren Emil tømrerbil og snedkerværksted"
    },
    {
      image: "https://assets.auxodev.dk/assets/f0baa3a2-6eb6-4b57-8f55-7b5fb1f2d6b7/montering-af-tegltag-med-ovenlys.jpg",
      alt: "Montering af nyt tegltag med ovenlysvinduer"
    },
    {
      image: "https://assets.auxodev.dk/assets/f0baa3a2-6eb6-4b57-8f55-7b5fb1f2d6b7/klassisk-vinterhave-med-havestue.jpg",
      alt: "Klassisk vinterhave og snedkerarbejde"
    }
  ];

  const handlePrevHeroSlide = () => {
    setCurrentHeroSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  };

  const handleNextHeroSlide = () => {
    setCurrentHeroSlide((prev) => (prev + 1) % heroSlides.length);
  };

  const testimonials = [
    {
      quote:
        "Som tømrermester ved Ribe hjælper vi private og erhverv med både mindre tømreropgaver og større byggeprojekter. Hos Tømrermester Søren Emil v/ Jacob Heiselberg får du faglig rådgivning, præcis udførelse og en løsning, der passer til bygningen, behovet og dit budget.",
      name: "Jacob Heiselberg",
      role: "Indehaver & Tømrermester",
      rating: "5.0",
      source: "Ribe & Sydvestjylland"
    },
    {
      quote:
        "Vi fik lagt nyt tegltag med ovenlysvinduer. Arbejdet blev udført til punkt og prikke, tidsplanen holdt, og kommunikationen var forbilledlig hele vejen igennem.",
      name: "Thomas & Mette",
      role: "Husejere i Ribe",
      rating: "5.0",
      source: "Nyt tegltag & ovenlys"
    },
    {
      quote:
        "Gennemført håndværk og professionel sparring fra start til slut på vores renovering. Jacob er altid til at få fat på og leverer tømrer- og snedkerarbejde af allerhøjeste kvalitet.",
      name: "Lars K.",
      role: "Boligejer i Esbjerg",
      rating: "5.0",
      source: "Renovering & tilbygning"
    }
  ];

  useEffect(() => {
    async function loadData() {
      const [servicesData, galleryData, sponsorData] = await Promise.all([
        fetchCmsCollection("services", { sort: "order", order: "asc" }),
        fetchCmsCollection("gallery", { sort: "order", order: "asc", limit: 8 }),
        fetchCmsCollection("sponsorships", { sort: "order", order: "asc", limit: 6 })
      ]);
      setServices(servicesData.items || []);
      setGalleryItems(galleryData.items || []);
      setSponsorships(sponsorData.items || []);
    }
    loadData();
  }, []);

  const openLightbox = (index) => {
    setCurrentImgIndex(index);
    setLightboxOpen(true);
  };

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": HOME_FAQS.map((faq) => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const activeReview = testimonials[currentTestimonial];

  return (
    <>
      <SEO
        title="Tømrermester i Ribe til nyt tag | Få fagligt overblik"
        description="Få en lokal tømrermester i Ribe til nyt tag, udskiftning af døre og vinduer eller renovering af hus. Få faglig rådgivning og et konkret tilbud på din opgave."
        ogTitle="Tømrer og snedker i Ribe, Esbjerg mm. - Prof. tømrerfirma"
        ogDescription="Professionelt tømrerfirma, der kombinerer solidt tømrerarbejde med præcision og faglig stolthed. Kontakt os her og få et tilbud i dag."
        schema={faqSchema}
      />

      {/* 1. FULL-WIDTH SPLIT HERO SECTION (40/60 Split Layout) */}
      <section className="relative min-h-[640px] lg:min-h-[740px] bg-[#122030] text-white overflow-hidden flex items-center">
        {/* Right Side Full-bleed Hero Image (~60% width on lg) */}
        <div className="absolute inset-y-0 right-0 w-full lg:w-[60%] z-0 overflow-hidden">
          <img
            key={currentHeroSlide}
            src={getOptimizedImageUrl(
              heroSlides[currentHeroSlide].image,
              { width: 1600 }
            )}
            alt={heroSlides[currentHeroSlide].alt}
            className="w-full h-full object-cover object-center transition-all duration-700 filter brightness-95"
          />
          {/* Soft dark gradient overlays near split boundary for seamless transition */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#122030] via-[#122030]/65 to-transparent lg:hidden"></div>
          <div className="hidden lg:block absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-[#122030] via-[#122030]/80 to-transparent"></div>
          <div className="hidden lg:block absolute inset-0 bg-stone-950/15 pointer-events-none"></div>
        </div>

        {/* Left Side Blueprint Background Pattern */}
        <div className="absolute inset-y-0 left-0 w-full lg:w-[45%] z-0 pointer-events-none">
          <div className="absolute inset-0 bg-[#122030] hidden lg:block"></div>
          <svg
            className="absolute inset-0 w-full h-full opacity-[0.07] text-white"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <defs>
              <pattern id="hero-blueprint-grid" width="48" height="48" patternUnits="userSpaceOnUse">
                <path d="M 48 0 L 0 0 0 48" fill="none" stroke="currentColor" strokeWidth="1" />
                <path d="M 0 24 L 48 24 M 24 0 L 24 48" fill="none" stroke="currentColor" strokeWidth="0.5" strokeDasharray="3 3" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#hero-blueprint-grid)" />
          </svg>
        </div>

        {/* Content aligned with site's standard container, extending slightly into the image area */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
          <div className="w-full lg:w-[55%] xl:w-[52%] space-y-6">
            {/* Small pill-style eyebrow label */}
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/10 border border-white/15 backdrop-blur-sm text-xs font-semibold uppercase tracking-wider text-stone-200 shadow-sm">
              <span className="w-2 h-2 rounded-full bg-[#ce0611]"></span>
              <span>Tømrer & Snedker i Ribe, Esbjerg & Bramming</span>
            </div>

            {/* Oversized bold display heading with tight line-height */}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tight text-white leading-[1.1]">
              Din foretrukne tømrer & snedker i Ribe, Esbjerg & Bramming
            </h1>

            {/* Short body paragraph */}
            <p className="text-base sm:text-lg text-stone-300 leading-relaxed max-w-xl">
              <strong className="text-white font-semibold">Tømrermester Søren Emil v/ Jacob Heiselberg</strong> er et professionelt tømrerfirma, der kombinerer solidt tømrerarbejde med præcision og faglig stolthed. Med erfaring som både tømrer og snedker leverer vi knivskarpe løsninger til både private og erhverv.
            </p>

            {/* Two horizontal CTA buttons */}
            <div className="pt-2 flex flex-wrap items-center gap-4">
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center px-6 py-3.5 rounded-md bg-[#ce0611] hover:bg-[#b0040e] text-white font-bold text-sm tracking-wide transition-all shadow-lg hover:shadow-red-600/25 hover:scale-[1.02] active:scale-[0.98]"
              >
                <span>Få et tilbud</span>
                <i className="fa-solid fa-arrow-right ml-2.5 text-xs" aria-hidden="true"></i>
              </Link>

              <a
                href={`tel:${COMPANY_INFO.phoneRaw}`}
                className="inline-flex items-center justify-center px-5 py-3.5 rounded-md bg-white/5 hover:bg-white/10 backdrop-blur-sm text-white font-semibold text-sm border border-white/20 hover:border-white/40 transition-all shadow-sm"
              >
                <i className="fa-solid fa-phone mr-2.5 text-xs text-stone-300" aria-hidden="true"></i>
                <span>Ring {COMPANY_INFO.phone}</span>
              </a>
            </div>

            {/* Thin horizontal divider line and two small trust items with icons */}
            <div className="pt-6 border-t border-white/15">
              <div className="flex flex-wrap items-center gap-6 sm:gap-10 text-xs sm:text-sm text-stone-300">
                <div className="flex items-center gap-2.5">
                  <span className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center text-[#ce0611] shrink-0">
                    <i className="fa-solid fa-shield-check text-xs" aria-hidden="true"></i>
                  </span>
                  <span className="font-medium">100% faglig stolthed & garanti</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <span className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center text-[#ce0611] shrink-0">
                    <i className="fa-solid fa-medal text-xs" aria-hidden="true"></i>
                  </span>
                  <span className="font-medium">15+ års erfaring i faget</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Compact bottom slider navigation control fixed to bottom edge at the 40/60 split (desktop only) */}
        <div className="hidden lg:flex absolute bottom-0 left-[40%] z-20 bg-[#0d1722] border-t border-r border-white/15">
          <button
            type="button"
            onClick={handlePrevHeroSlide}
            className="p-4 hover:bg-white/10 text-stone-300 hover:text-white transition-colors border-r border-white/10 focus:outline-none focus:bg-white/15"
            aria-label="Forrige slide"
          >
            <i className="fa-solid fa-arrow-left text-sm" aria-hidden="true"></i>
          </button>
          <button
            type="button"
            onClick={handleNextHeroSlide}
            className="p-4 hover:bg-white/10 text-stone-300 hover:text-white transition-colors focus:outline-none focus:bg-white/15"
            aria-label="Næste slide"
          >
            <i className="fa-solid fa-arrow-right text-sm" aria-hidden="true"></i>
          </button>
        </div>
      </section>

      {/* 2. TWO-COLUMN STAGGERED SECTION (Restyled to design reference #c5c5698c) */}
      <section className="py-20 sm:py-28 bg-stone-50 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            {/* Left Column: Heading + Body Text + Wide Landscape Image */}
            <div className="space-y-8">
              <div className="space-y-5">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-stone-900 leading-[1.12]">
                  Tømrermester ved Ribe til nyt tag og gennemtænkt byggeri
                </h2>
                <p className="text-base sm:text-lg text-stone-700 leading-relaxed">
                  Som tømrermester ved Ribe hjælper vi private og erhverv med både mindre tømreropgaver og større byggeprojekter. Hos Tømrermester Søren Emil v/ Jacob Heiselberg får du faglig rådgivning, grundig planlægning og et resultat udført med solid præcision.
                </p>
                <p className="text-stone-600 text-sm sm:text-base leading-relaxed">
                  Vi kombinerer klassiske håndværksdyder med moderne byggemetoder. Uanset om du skal have nyt tag, monteret nye vinduer eller renoveret hele boligen, sikrer vi en tryg byggeproces fra første opmåling til den sidste finish.
                </p>
              </div>

              {/* Wide landscape-oriented image with sharp corners / minimal border-radius */}
              <div className="w-full aspect-[16/10] overflow-hidden rounded-sm border border-stone-200 bg-stone-200 shadow-sm">
                <img
                  src={getOptimizedImageUrl(
                    "https://assets.auxodev.dk/assets/f0baa3a2-6eb6-4b57-8f55-7b5fb1f2d6b7/nyt-tegltag-paa-villa-400x284.jpg",
                    { width: 900 }
                  )}
                  alt="Nyt tegltag på villa"
                  className="w-full h-full object-cover hover:scale-102 transition-transform duration-500"
                />
              </div>
            </div>

            {/* Right Column (Offset Vertically): Portrait Image + Body Text + Bulleted List + Ghost CTA */}
            <div className="space-y-8 lg:pt-16">
              {/* Portrait-oriented image */}
              <div className="w-full aspect-[4/3] sm:aspect-[16/11] lg:aspect-[4/3] overflow-hidden rounded-sm border border-stone-200 bg-stone-200 shadow-sm">
                <img
                  src={getOptimizedImageUrl(
                    "https://assets.auxodev.dk/assets/f0baa3a2-6eb6-4b57-8f55-7b5fb1f2d6b7/stillads-tomrervogn-soren-emil.jpg",
                    { width: 900 }
                  )}
                  alt="Professionelt tømrer- og facadearbejde med stillads og tømrervogn"
                  className="w-full h-full object-cover hover:scale-102 transition-transform duration-500"
                />
              </div>

              <div className="space-y-6">
                <p className="text-stone-700 text-base leading-relaxed">
                  Vi lægger vægt på den tætte dialog og det gode håndværk gennem hele byggeprocessen. Uanset opgavens størrelse sikrer vi klare aftaler, gennemsigtige priser og holdbare løsninger, der holder i årtier.
                </p>

                {/* Bulleted list with checkmark icons */}
                <ul className="space-y-3 pt-1">
                  <li className="flex items-start text-stone-800 text-sm sm:text-base">
                    <span className="w-5 h-5 rounded-full bg-red-100 text-[#ce0611] flex items-center justify-center mr-3 mt-0.5 shrink-0 text-xs">
                      <i className="fa-solid fa-check" aria-hidden="true"></i>
                    </span>
                    <span>Gennemført tømrer- og snedkerarbejde af højeste standard</span>
                  </li>
                  <li className="flex items-start text-stone-800 text-sm sm:text-base">
                    <span className="w-5 h-5 rounded-full bg-red-100 text-[#ce0611] flex items-center justify-center mr-3 mt-0.5 shrink-0 text-xs">
                      <i className="fa-solid fa-check" aria-hidden="true"></i>
                    </span>
                    <span>Erfaren rådgivning om materialevalg, isolering og levetid</span>
                  </li>
                  <li className="flex items-start text-stone-800 text-sm sm:text-base">
                    <span className="w-5 h-5 rounded-full bg-red-100 text-[#ce0611] flex items-center justify-center mr-3 mt-0.5 shrink-0 text-xs">
                      <i className="fa-solid fa-check" aria-hidden="true"></i>
                    </span>
                    <span>Total- og fagentreprise i Ribe, Esbjerg og omegn</span>
                  </li>
                  <li className="flex items-start text-stone-800 text-sm sm:text-base">
                    <span className="w-5 h-5 rounded-full bg-red-100 text-[#ce0611] flex items-center justify-center mr-3 mt-0.5 shrink-0 text-xs">
                      <i className="fa-solid fa-check" aria-hidden="true"></i>
                    </span>
                    <span>Punktlighed, grundig oprydning og fast kontaktperson</span>
                  </li>
                </ul>

                {/* Ghost outlined CTA button */}
                <div className="pt-3">
                  <Link
                    to="/vi-tilbyder"
                    className="inline-flex items-center justify-center px-7 py-3 rounded-full border border-stone-800 text-stone-900 font-semibold text-xs uppercase tracking-wider hover:bg-stone-900 hover:text-white transition-all shadow-2xs"
                  >
                    <span>LÆS MERE OM VORES YDELSER</span>
                    <i className="fa-solid fa-arrow-right ml-2 text-xs" aria-hidden="true"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. CORE SERVICES SECTION */}
      <section className="py-16 sm:py-24 bg-stone-100/70 border-t border-stone-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-stone-900 tracking-tight">
              Vi tilbyder skræddersyede løsninger
            </h2>
            <p className="text-stone-600 text-sm sm:text-base mt-2">
              Uanset om du skal have nyt tag, monteret nye vinduer eller renoveret boligen, sikrer vi solidt håndværk og gennemtænkt rådgivning.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            {(services.length > 0 ? services : DEFAULT_SERVICES).map((srv, idx, arr) => {
              const total = arr.length;

              // On md (2 items per row in a 12-col grid):
              const mdRemainder = total % 2;
              const isLastOnMd = mdRemainder === 1 && idx === total - 1;
              const mdSpan = isLastOnMd ? "md:col-span-12" : "md:col-span-6";

              // On lg (3 items per normal row -> 4 columns each in a 12-col grid):
              const lgRemainder = total % 3;
              let lgSpan = "lg:col-span-4";
              if (lgRemainder === 1 && idx === total - 1) {
                // If there is 1 box in the last row, make it fill col-12
                lgSpan = "lg:col-span-12";
              } else if (lgRemainder === 2 && idx >= total - 2) {
                // If there are 2 boxes in the last row, make them fill col-6 each
                lgSpan = "lg:col-span-6";
              }

              const imgUrl = Array.isArray(srv.featured_image)
                ? srv.featured_image[0]
                : Array.isArray(srv.image)
                ? srv.image[0]
                : srv.image || "https://assets.auxodev.dk/assets/f0baa3a2-6eb6-4b57-8f55-7b5fb1f2d6b7/stillads-tomrervogn-soren-emil.jpg";

              const serviceSlug =
                srv.service_slug ||
                (typeof srv.slug === "object" ? srv.slug?._ : srv.slug) ||
                "vi-tilbyder";
              const linkTarget = serviceSlug.startsWith("/") ? serviceSlug : `/${serviceSlug}`;

              const tag =
                srv.tag ||
                (srv.gallery_tag === "tag"
                  ? "Tagarbejde"
                  : srv.gallery_tag === "vinduer-doere"
                  ? "Vinduer & Døre"
                  : srv.gallery_tag === "renovering"
                  ? "Tømrer & Renovering"
                  : srv.gallery_tag === "tilbygning-vinterhave"
                  ? "Tilbygning"
                  : srv.gallery_tag === "snedkerarbejde"
                  ? "Snedkerarbejde"
                  : "Tømrerarbejde");

              return (
                <div
                  key={srv.id || srv.service_slug || idx}
                  className={`${mdSpan} ${lgSpan} h-full bg-white rounded-2xl border border-stone-200/90 shadow-xs overflow-hidden flex flex-col group hover:shadow-md transition-shadow`}
                >
                  <div className="h-52 sm:h-56 md:h-60 w-full shrink-0 overflow-hidden bg-stone-100 relative">
                    <img
                      src={getOptimizedImageUrl(imgUrl, { width: 900 })}
                      alt={srv.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-3 left-3 bg-[#192a3e]/90 backdrop-blur-sm text-red-400 text-xs font-semibold px-2.5 py-1 rounded-full">
                      {tag}
                    </div>
                  </div>
                  <div className="p-6 flex-1 flex flex-col justify-between">
                    <div>
                      <h3 className="text-xl font-bold text-stone-900 mb-2">{srv.title}</h3>
                      <p className="text-stone-600 text-sm leading-relaxed mb-4">
                        {srv.short_description || srv.desc || "Solidt håndværk og professionel rådgivning."}
                      </p>
                    </div>
                    <Link
                      to={linkTarget}
                      className="inline-flex items-center text-sm font-semibold text-[#ce0611] hover:text-[#b0040e] transition-colors mt-2"
                    >
                      <span>{srv.linkText || `Læs mere om ${srv.title.toLowerCase()}`}</span>
                      <i className="fa-solid fa-arrow-right ml-2 text-xs" aria-hidden="true"></i>
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/vi-tilbyder"
              className="inline-flex items-center justify-center px-6 py-3 rounded-full bg-stone-900 hover:bg-stone-800 text-white font-medium text-sm transition-colors"
            >
              <span>Se alle vores ydelser & specialer</span>
              <i className="fa-solid fa-chevron-right ml-2 text-xs" aria-hidden="true"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* 4. EXPERT POSITIONING / ENTERPRISE ADVICE */}
      <section className="py-16 sm:py-24 bg-white border-t border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 space-y-6">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-stone-100 text-stone-800 text-xs font-semibold uppercase tracking-wider">
                <i className="fa-solid fa-cubes text-[#ce0611]" aria-hidden="true"></i>
                <span>Gennemskuelig proces</span>
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-stone-900 tracking-tight">
                Tryghed og kvalitet i hver eneste detalje
              </h2>
              <p className="text-stone-600 text-sm sm:text-base leading-relaxed">
                Når du vælger Tømrermester Søren Emil, får du mere end blot en håndværker – du får en engageret samarbejdspartner, der tager hånd om projektet fra start til slut. Vi lægger vægt på gennemskuelige aftaler, præcise tidsplaner og et resultat, der holder i mange år.
              </p>

              <div className="space-y-3 pt-2">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-red-100 text-[#ce0611] flex items-center justify-center shrink-0 mt-0.5">
                    <i className="fa-solid fa-check text-xs" aria-hidden="true"></i>
                  </div>
                  <div>
                    <h4 className="text-stone-900 font-semibold text-sm">Grundig forventningsafstemning</h4>
                    <p className="text-xs text-stone-600">Vi gennemgår projektet sammen med dig på adressen og afklarer ønsker, materialer og tidsramme.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-red-100 text-[#ce0611] flex items-center justify-center shrink-0 mt-0.5">
                    <i className="fa-solid fa-check text-xs" aria-hidden="true"></i>
                  </div>
                  <div>
                    <h4 className="text-stone-900 font-semibold text-sm">Uforpligtende og specificeret tilbud</h4>
                    <p className="text-xs text-stone-600">Du modtager et gennemskueligt tilbud uden ubehagelige overraskelser.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-red-100 text-[#ce0611] flex items-center justify-center shrink-0 mt-0.5">
                    <i className="fa-solid fa-check text-xs" aria-hidden="true"></i>
                  </div>
                  <div>
                    <h4 className="text-stone-900 font-semibold text-sm">Professionel udførelse og oprydning</h4>
                    <p className="text-xs text-stone-600">Vi møder til tiden, arbejder effektivt og efterlader altid byggepladsen pæn og ryddelig.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="p-6 rounded-2xl bg-stone-50 border border-stone-200">
                <i className="fa-solid fa-ruler-combined text-2xl text-[#ce0611] mb-3" aria-hidden="true"></i>
                <h3 className="font-bold text-stone-900 text-base mb-1.5">Faglig rådgivning</h3>
                <p className="text-xs text-stone-600 leading-relaxed">
                  Vi hjælper med at vælge de rigtige materialer og løsninger, der passer til boligens arkitektur og budget.
                </p>
              </div>

              <div className="p-6 rounded-2xl bg-stone-50 border border-stone-200">
                <i className="fa-solid fa-shield-halved text-2xl text-[#ce0611] mb-3" aria-hidden="true"></i>
                <h3 className="font-bold text-stone-900 text-base mb-1.5">Holdbare materialer</h3>
                <p className="text-xs text-stone-600 leading-relaxed">
                  Vi samarbejder med anerkendte producenter for at sikre et langtidsholdbart og energieffektivt resultat.
                </p>
              </div>

              <div className="p-6 rounded-2xl bg-stone-50 border border-stone-200">
                <i className="fa-solid fa-calendar-check text-2xl text-[#ce0611] mb-3" aria-hidden="true"></i>
                <h3 className="font-bold text-stone-900 text-base mb-1.5">Tydelige aftaler</h3>
                <p className="text-xs text-stone-600 leading-relaxed">
                  Klare tidsplaner og specificerede tilbud, så du altid ved præcis, hvad der indgår i opgaven.
                </p>
              </div>

              <div className="p-6 rounded-2xl bg-stone-50 border border-stone-200">
                <i className="fa-solid fa-handshake-angle text-2xl text-[#ce0611] mb-3" aria-hidden="true"></i>
                <h3 className="font-bold text-stone-900 text-base mb-1.5">Én fast kontaktperson</h3>
                <p className="text-xs text-stone-600 leading-relaxed">
                  Gennemgående dialog direkte med tømrermesteren fra første besigtigelse til den endelige aflevering.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. SELECTED PROJECTS SHOWCASE (Restyled to design reference #796559f0) */}
      <section className="py-20 sm:py-28 bg-stone-100/70 border-t border-stone-200 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full max-w-full">
          {/* Prominent, large-scale heading top left with bright accent period */}
          <div className="max-w-3xl mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-stone-900 tracking-tight">
              Udvalgte projekter og referencer<span className="text-[#ce0611]">.</span>
            </h2>
            <p className="text-stone-600 text-base sm:text-lg mt-2">
              Et kig på vores seneste tagarbejder, tilbygninger, snedkerløsninger og renoveringer i Ribe og omegn.
            </p>
          </div>

          {/* Asymmetric grid: 1 large feature image left spanning full height + 4 smaller images in 2x2 grid right */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 sm:gap-6 w-full max-w-full">
            {/* Feature Left Image */}
            <div className="lg:col-span-6 w-full max-w-full">
              {galleryItems[0] && (
                <div
                  onClick={() => openLightbox(0)}
                  className="group relative rounded-3xl overflow-hidden bg-stone-200 border border-stone-300/80 aspect-[4/3] sm:aspect-square lg:aspect-auto lg:h-full min-h-[220px] sm:min-h-[360px] cursor-pointer shadow-sm hover:shadow-md transition-all w-full max-w-full"
                >
                  <img
                    src={getOptimizedImageUrl(galleryItems[0].image?.[0] || "", { width: 900 })}
                    alt={galleryItems[0].alt || galleryItems[0].title || "Hovedprojekt"}
                    className="w-full h-full max-w-full object-cover group-hover:scale-105 transition-transform duration-300 block"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-5 sm:p-6 text-white">
                    <span className="font-bold text-base sm:text-lg">{galleryItems[0].title}</span>
                    <span className="text-xs text-red-300 flex items-center mt-1">
                      <i className="fa-solid fa-magnifying-glass-plus mr-1.5" aria-hidden="true"></i>
                      Klik for at se i fuld skærm
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Right 2x2 grid */}
            <div className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6 w-full max-w-full">
              {galleryItems.slice(1, 5).map((item, index) => {
                const actualIndex = index + 1;
                const imgUrl = item.image?.[0] || "";
                return (
                  <div
                    key={item.id || actualIndex}
                    onClick={() => openLightbox(actualIndex)}
                    className="group relative rounded-3xl overflow-hidden bg-stone-200 border border-stone-300/80 aspect-[4/3] cursor-pointer shadow-sm hover:shadow-md transition-all w-full max-w-full"
                  >
                    <img
                      src={getOptimizedImageUrl(imgUrl, { width: 600 })}
                      alt={item.alt || item.title || "Projekt"}
                      className="w-full h-full max-w-full object-cover group-hover:scale-105 transition-transform duration-300 block"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-4 text-white">
                      <span className="font-semibold text-sm line-clamp-1">{item.title}</span>
                      <span className="text-[11px] text-red-300 flex items-center mt-0.5">
                        <i className="fa-solid fa-magnifying-glass-plus mr-1" aria-hidden="true"></i>
                        Klik for at forstørre
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Centered pill-shaped primary action button below image grid */}
          <div className="flex justify-center mt-12 sm:mt-16">
            <Link
              to="/galleri"
              className="inline-flex items-center justify-center px-8 py-3.5 rounded-full bg-stone-900 hover:bg-stone-800 text-white font-medium text-sm transition-all shadow-sm hover:shadow hover:scale-[1.02] active:scale-[0.98]"
            >
              <span>Se hele galleriet</span>
              <i className="fa-solid fa-arrow-right ml-2.5 text-xs text-red-400" aria-hidden="true"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* 6. CSR & LOCAL SPONSORSHIPS HIGHLIGHT */}
      <section className="py-16 sm:py-20 bg-white border-t border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 tracking-tight">
              Vi støtter op om bæredygtighed og lokalsamfundet
            </h2>
            <p className="text-stone-600 text-sm mt-2">
              Tømrermester Jacob bakker op om miljøvenlige initiativer, affaldssortering og sunde lokalsamfund.
            </p>
          </div>

          <div className="py-10 px-6 bg-stone-50 rounded-xl border border-stone-200 text-center flex items-center justify-center">
            <span className="text-stone-400 font-semibold text-sm sm:text-base tracking-widest uppercase">
              LOGO KOMMER SNART
            </span>
          </div>

          <div className="text-center mt-8">
            <Link
              to="/csr"
              className="inline-flex items-center text-xs font-semibold text-[#ce0611] hover:text-[#b0040e] transition-colors uppercase tracking-wider"
            >
              <span>Se alle sponsorater & CSR-mærker</span>
              <i className="fa-solid fa-arrow-right ml-1.5" aria-hidden="true"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* 7. FAQ ACCORDION SECTION (Restyled to design reference #c1c98db1) */}
      <FAQSection faqs={HOME_FAQS} />

      {/* 8. QUICK CONTACT BANNER */}
      <QuickContactBanner />

      {/* Lightbox Modal for Gallery Showcase */}
      <LightboxModal
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        images={galleryItems.map((g) => ({
          url: g.image?.[0],
          title: g.title,
          alt: g.alt || g.title
        }))}
        currentIndex={currentImgIndex}
        setCurrentIndex={setCurrentImgIndex}
      />
    </>
  );
}
